import {
	auditService,
	GET_CASE_DOCUMENTS,
	GET_CASE_STEPS_STATUS,
	GET_LAWYER_CASE_DETAILS,
} from '@/api';
import { casesLawyerService } from '@/api/casesLawyer';
import { Button, ButtonDropdownConfirm, Typography } from '@/components/common';
import { ErrorIcon } from '@/components/dashboard';
import Icons from '@/const/icons';
import { CaseContext } from '@/context/CaseContext';
import { useNotification, usePhaseError } from '@/hooks';
import {
	CaseDetailsLawyer,
	CaseSettlementDetails,
	DocumentActivePhase,
	DocumentType,
	PaymentMethodEnum,
	PhaseNameCase,
	StatusPhase,
	StepNameCase,
	StepStatusCase,
	StepStatusCaseType,
} from '@/interfaces';
import {
	getIsStepApproved,
	getLatestDocumentByType,
	getLatestOtpByType,
	getStep,
} from '@/lib';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { useContext } from 'react';
import { getValidCaseId } from '@/utils';
import { PhaseStatusBadge } from '../phase-status-badge/PhaseStatusBadge';
import { SaveStatus } from '../SaveStatus';
interface Props {
	title: PhaseNameCase;
	classNameContainer?: string;
	children: React.ReactNode;
	caseDetails: CaseDetailsLawyer | undefined;
	phaseSteps?: StepStatusCase[] | undefined;
	statusPhase: StepStatusCaseType;
	caseDocuments?: DocumentActivePhase[] | undefined;
	settlementDetail?: CaseSettlementDetails;
	setIsErrorMessage: (value: boolean) => void;
	phaseData?: StatusPhase[];
	hasPaymentError?: boolean;
	phaseNum?: string;
	finishPhaseDate?: string | undefined | null;
	className?: string;
}

export const ContainerPhase: React.FC<Props> = ({
	title,
	classNameContainer,
	children,
	caseDetails,
	statusPhase,
	phaseSteps,
	caseDocuments,
	settlementDetail,
	setIsErrorMessage,
	phaseData,
	hasPaymentError,
	phaseNum,
	finishPhaseDate,
	className,
}) => {
	const { isCaseReadOnly } = useContext(CaseContext);
	const [caseDocumentByStep, setCaseDocumentByStep] =
		React.useState<DocumentActivePhase>();
	const [optByStep, setOptByStep] = React.useState<DocumentActivePhase>();
	const phaseHideActionsButtons: PhaseNameCase[] = [
		'Investigation Phase',
		'Applicant Intake',
	];

	const {
		getPhaseToPutErrorMessage,
		handleSetErrorMessage,
		isErrorPhaseMessage,
		errorPhaseMessage,
	} = usePhaseError({
		phase: phaseData,
		phaseToValidate: title,
	});

	const showPhaseErrorMessage = () => {
		return (
			isErrorPhaseMessage &&
			errorPhaseMessage &&
			getPhaseToPutErrorMessage() &&
			title.includes(getPhaseToPutErrorMessage() || '')
		);
	};

	React.useEffect(() => {
		if (
			caseDocuments &&
			caseDetails &&
			caseDetails.case_type &&
			title === 'Active Case Phase'
		) {
			const file = getLatestDocumentByType(
				caseDocuments,
				caseDetails?.case_type
			);
			const otp = getLatestOtpByType(caseDocuments);
			setOptByStep(otp);
			setCaseDocumentByStep(file);
		}
	}, [caseDocuments, title]);

	return (
		<>
			<div
				id="container-phase"
				className={clsx(
					'border rounded-3xl border-green-secondary py-8 bg-white max-w-[837px]',
					{
						'!bg-gray-20': isCaseReadOnly,
					},
					{
						'!bg-gray-15': hasPaymentError,
					},
					{
						'border-main-red border-2':
							showPhaseErrorMessage() || hasPaymentError,
					},
					className
				)}
				tabIndex={0}
			>
				<div className="px-8 pb-8">
					<div className="flex w-full items-center justify-between">
						<Typography type="regular-18" className="whitespace-nowrap">
							{`${phaseNum ? `Phase ${phaseNum}:` : ''} ${title}`}
						</Typography>
						{!phaseHideActionsButtons.includes(title) ? (
							<ActionButtons
								title={title}
								caseDetails={caseDetails}
								statusPhase={statusPhase}
								phaseSteps={phaseSteps}
								setIsErrorMessage={setIsErrorMessage}
								caseDocumentByStep={caseDocumentByStep}
								caseDocuments={caseDocuments}
								optByStep={optByStep}
								settlementDetail={settlementDetail}
								handleSetErrorMessage={handleSetErrorMessage}
								hasPaymentError={hasPaymentError}
							/>
						) : (
							<PhaseStatusBadge isCompleted={Boolean(finishPhaseDate)} />
						)}
					</div>
				</div>
				<div
					className={clsx(
						'px-6 rounded-b-[32px] space-y-8',
						// caseDetails?.phase_name && title.includes(caseDetails.phase_name)
						// 	? 'bg-gray-15'
						// 	: 'bg-white',
						classNameContainer,
						{
							'!bg-gray-20': isCaseReadOnly,
						},
						{
							'!bg-gray-15': hasPaymentError,
						}
					)}
				>
					{children}
					{phaseHideActionsButtons.includes(title) && (
						<div className="flex items-center justify-between">
							<SaveStatus />
							<ActionButtons
								title={title}
								caseDetails={caseDetails}
								statusPhase={statusPhase}
								phaseSteps={phaseSteps}
								setIsErrorMessage={setIsErrorMessage}
								caseDocumentByStep={caseDocumentByStep}
								caseDocuments={caseDocuments}
								optByStep={optByStep}
								settlementDetail={settlementDetail}
								handleSetErrorMessage={handleSetErrorMessage}
								hasPaymentError={hasPaymentError}
							/>
						</div>
					)}
				</div>
			</div>
			{showPhaseErrorMessage() && (
				<Typography
					type="regular-16"
					className={clsx(
						'text-main-red mt-2 transition-opacity duration-500 ease-in-out transform',
						isErrorPhaseMessage
							? 'opacity-100 translate-y-0'
							: 'opacity-0 -translate-y-2'
					)}
				>
					{errorPhaseMessage}
				</Typography>
			)}
		</>
	);
};

interface ButtonsProps {
	title: PhaseNameCase;
	caseDetails: CaseDetailsLawyer | undefined;
	statusPhase: StepStatusCaseType;
	phaseSteps: StepStatusCase[] | undefined;
	caseDocumentByStep?: DocumentActivePhase | undefined;
	caseDocuments?: DocumentActivePhase[] | undefined;
	optByStep?: DocumentActivePhase | undefined;
	settlementDetail?: CaseSettlementDetails;
	setIsErrorMessage: (value: boolean) => void;
	hasPaymentError?: boolean;
	handleSetErrorMessage: () => void;
}

export const ActionButtons: React.FC<ButtonsProps> = ({
	title,
	caseDetails,
	statusPhase,
	phaseSteps,
	caseDocumentByStep,
	caseDocuments,
	optByStep,
	setIsErrorMessage,
	settlementDetail,
	hasPaymentError,
	handleSetErrorMessage,
}) => {
	const router = useRouter();
	const { id } = router.query;
	const { toastError, toastSuccess } = useNotification();
	const { isCaseReadOnly } = useContext(CaseContext);
	const queryClient = useQueryClient();

	const nextToNextPhase = [
		'Investigation Phase',
		'Applicant Intake',
		'Active Case Phase',
	];
	const isMovedToNextPhase = nextToNextPhase.includes(title);

	const isApproved = statusPhase === 'approved';

	const optRequiredStepStatus = getStep('Opt Out Request', phaseSteps)?.status;

	const [isSendingDocument, setIsSendingDocument] =
		React.useState<boolean>(false);

	const getIsPhaseMoved = (): boolean => {
		const currentPhase = caseDetails?.phase_name || '';
		const indexCurrentPhase = nextToNextPhase.findIndex((item) =>
			item.includes(currentPhase)
		);

		if (!title.includes(currentPhase) && nextToNextPhase.includes(title)) {
			if (indexCurrentPhase === -1) {
				return true;
			} else if (title === 'Investigation Phase') {
				return true;
			} else if (title === 'Applicant Intake' && indexCurrentPhase > 1) {
				return true;
			} else if (title === 'Active Case Phase' && indexCurrentPhase > 2) {
				return true;
			} else {
				return false;
			}
		} else {
			return false;
		}
	};

	const refetchQueries = () => {
		const caseId = getValidCaseId(id);
		if (!caseId) return;
		queryClient.refetchQueries({ queryKey: [GET_CASE_STEPS_STATUS, caseId] });
		queryClient.refetchQueries({ queryKey: [GET_LAWYER_CASE_DETAILS, caseId] });
	};

	const getIsMoveNextPhaseDisabled = (): boolean => {
		if (isCaseReadOnly) {
			return true;
		} else if (title === 'Applicant Intake Phase') {
			return (
				caseDetails?.is_draft ||
				caseDetails?.status?.name !== 'Closed to new clients' ||
				!isApproved ||
				caseDetails?.progress_name === 'Case Aborted' ||
				getIsPhaseMoved()
			);
		} else if (title === 'Active Case Phase') {
			return (
				caseDetails?.progress_name === 'Case Aborted' ||
				caseDetails?.progress_name === 'Case Unsuccessful' ||
				caseDetails?.phase_name !== 'Active Case' ||
				// !isApproved ||
				!caseDocumentByStep?.sent_at ||
				(optRequiredStepStatus !== 'default' && !optByStep?.sent_at) ||
				getIsPhaseMoved()
			);
		} else {
			return !isApproved || getIsPhaseMoved();
		}
	};

	// const getIsCloseNewClientDisabled = (): boolean => {
	// 	return (
	// 		!isApproved ||
	// 		isCaseReadOnly ||
	// 		caseDetails?.is_draft ||
	// 		caseDetails?.status?.name === 'Closed to new clients'
	// 	);
	// };

	const getIsGoLiveDisabled = (): boolean => {
		return (
			!isApproved ||
			isCaseReadOnly ||
			!caseDetails?.is_draft ||
			caseDetails.phase_name !== 'Applicant Intake'
		);
	};

	const getIsSendComplaintDisabled = (): boolean => {
		const evaluateName =
			caseDetails?.case_type === 'CLASS_ACTION' ? 'Complaint' : 'Demand Letter';
		const isApproved = getIsStepApproved(evaluateName, phaseSteps);
		const isCurrentPhase = caseDetails?.phase_name === 'Active Case';
		const optVerification =
			optRequiredStepStatus !== 'default' &&
			optRequiredStepStatus !== 'approved';
		return !isApproved || !isCurrentPhase || isCaseReadOnly || optVerification;
	};

	const getIsSendOptHidden = (): boolean => {
		return optRequiredStepStatus === 'default';
	};

	const getIsCaseUnsuccessfulDisabled = (): boolean => {
		const evaluateName =
			caseDetails?.case_type === 'CLASS_ACTION' ? 'Complaint' : 'Demand Letter';
		const isApproved = getIsStepApproved(evaluateName, phaseSteps);
		return (
			!isApproved ||
			isCaseReadOnly ||
			caseDetails?.phase_name !== 'Active Case' ||
			(optRequiredStepStatus !== 'default' && !optByStep?.sent_at) ||
			!caseDocumentByStep?.sent_at
		);
	};

	const getIsSentSettlementAgreementDisabled = () => {
		return (
			!isApproved ||
			isCaseReadOnly ||
			!!caseDetails?.date_settlement_sent ||
			caseDetails?.phase_name !== 'Settlement'
		);
	};

	const getIsPaySettlementDisabled = () => {
		return (
			!isApproved ||
			isCaseReadOnly ||
			!caseDetails?.date_settlement_sent ||
			!!caseDetails?.date_payment_sent ||
			caseDetails?.phase_name !== 'Settlement'
		);
	};

	const getIsCaseResolvedDisabled = () => {
		const isSettlementApproved = getIsStepApproved(
			'Payment Details',
			phaseSteps
		);
		const isPaymentDetailsApproved = getIsStepApproved(
			'Payment Details',
			phaseSteps
		);
		const isExternal =
			settlementDetail?.payment_method === PaymentMethodEnum.EXTERNAL;

		return (
			!isApproved ||
			isCaseReadOnly ||
			!isSettlementApproved ||
			!isPaymentDetailsApproved ||
			!caseDetails?.date_settlement_sent ||
			(!isExternal && !caseDetails?.date_payment_sent) ||
			caseDetails?.phase_name !== 'Settlement' ||
			!!caseDetails?.date_case_resolved
		);
	};

	const handleMoveToTheNextPhase = async () => {
		try {
			if (id) {
				await auditService.patchMoveToTheNextPhase(id as string);
				refetchQueries();
				toastSuccess('You have successfully advanced to the next phase.');
			}
		} catch (error) {
			toastError(
				'An error occurred while advancing to the next phase. Please try again.'
			);
		}
	};

	const handleGoLive = async () => {
		try {
			if (id) {
				await auditService.patchGoLive(id as string);
				refetchQueries();
				toastSuccess(
					<span>
						Your case is now live on the landing page. <br /> You can no longer
						edit the case name.
					</span>
				);
			}
		} catch (error) {
			toastError(
				'An error occurred while making your case live on the landing page. Please try again.'
			);
		}
	};

	// const handleCloseToNewClients = async () => {
	// 	try {
	// 		if (id) {
	// 			await auditService.patchCloseStatus(id as string);
	// 			refetchQueries();
	// 			toastSuccess('You have successfully closed the case to new clients.');
	// 		}
	// 	} catch (error) {
	// 		toastError(
	// 			'An error occurred while closing the case to new clients. Please try again.'
	// 		);
	// 	}
	// };

	const handleSendOptRequest = async () => {
		try {
			await casesLawyerService.sendActiveCaseDocument(id as string, 'Opt');
			refetchQueries();
			queryClient.refetchQueries({ queryKey: [GET_CASE_DOCUMENTS] });
			toastSuccess('The Opt Out Request has been successfully sent.');
		} catch (error) {
			toastError(
				`Error: The Opt Out Request could not be sent. Please try again.`
			);
		}
	};

	const handleUnsuccessful = async () => {
		try {
			await casesLawyerService.unsuccessful(id as string);
			refetchQueries();
			toastSuccess('You have successfully mark the case as unsuccessful.');
		} catch (error) {
			toastError(
				'An issue occurred while marking the case as unsuccessful. Please try again.'
			);
		}
	};

	const handleSendDocument = async (type: DocumentType) => {
		setIsSendingDocument(true);
		try {
			await casesLawyerService.sendActiveCaseDocument(id as string, type);
			refetchQueries();
			queryClient.refetchQueries({ queryKey: [GET_CASE_DOCUMENTS] });
			toastSuccess(`The ${type.toLowerCase()} has been sent successfully`);
		} catch (error) {
			toastError(
				`Error: The ${type.toLowerCase()} could not be sent. Please try again.`
			);
		} finally {
			setTimeout(() => {
				setIsSendingDocument(false);
			}, 500);
		}
	};

	const handleSendSettlementAgreement = async () => {
		try {
			await casesLawyerService.confirmSettlementAgreement(id as string);
			refetchQueries();
			toastSuccess('The Settlement Agreement has been successfully sent.');
		} catch (error) {
			toastError(
				'An error occurred while sending Settlement Agreement. Please try again.'
			);
		}
	};

	const handlePaySettlement = async () => {
		try {
			await casesLawyerService.confirmPaySettlement(id as string);
			refetchQueries();
			toastSuccess('The settlement has been successfully paid.');
		} catch (error) {
			toastError(
				'An error occurred while processing the settlement payment. Please try again.'
			);
		}
	};

	const handleCaseResolved = async () => {
		try {
			await casesLawyerService.resolvedCase(id as string);
			refetchQueries();
			toastSuccess('The case has been successfully resolved and closed.');
		} catch (error) {
			toastError(
				'An error occurred while resolving and closing the case. Please try again.'
			);
		}
	};

	const getDocumentByStep = (stepName: StepNameCase) => {
		return caseDocuments?.find((doc) => doc.document_type === stepName);
	};
	return (
		<div className="flex items-center w-full justify-end flex-wrap gap-4 2xl:gap-6">
			{title === 'Applicant Intake' && (
				<>
					<Button
						label="Previous"
						decoration="line-primary"
						className="!w-[150px]"
						size="medium"
						iconLeft
						icon={Icons.arrowLeft}
						classNameButton="!size-4 mr-4"
					/>

					{caseDetails?.is_draft && (
						<ButtonDropdownConfirm
							label="Publish Case"
							confirmMessage="Confirm to Go Live."
							buttonLabel="Confirm"
							disabled={!caseDetails?.is_draft}
							incompleted={getIsGoLiveDisabled()}
							onClickConfirm={handleGoLive}
							onClickButton={() => {
								// getIsGoLiveDisabled() && setIsErrorMessage(true);
								if (getIsGoLiveDisabled()) {
									setIsErrorMessage(true);
									if (isApproved) {
										handleSetErrorMessage();
									}
								}
							}}
							decoration="primary"
							classNameDropdownMenuContent="w-[205px]"
							buttonIcon={Icons.checkWhite}
							buttonIconRight={!caseDetails?.is_draft}
						/>
					)}
					{/* TODO: //! Check implementation to sent status */}
					{/* <ButtonDropdownConfirm
						label="Closed to new Clients"
						confirmMessage="Confirm to Close to New Clients."
						buttonLabel="Confirm"
						disabled={caseDetails?.status?.name === 'Closed to new clients'}
						incompleted={getIsCloseNewClientDisabled()}
						onClickConfirm={handleCloseToNewClients}
						onClickButton={() => {
							getIsCloseNewClientDisabled() && setIsErrorMessage(true);
						}}
						decoration="primary"
						classNameDropdownMenuContent="w-[205px]"
						buttonIcon={Icons.checkWhite}
						buttonIconRight={
							caseDetails?.status?.name === 'Closed to new clients'
						}
					/> */}
				</>
			)}

			{title === 'Active Case Phase' && (
				<>
					{phaseSteps?.map((item) => {
						const type = item.name;
						return (
							(type === 'Demand Letter' || type === 'Complaint') && (
								<React.Fragment key={'button-' + type}>
									<ButtonDropdownConfirm
										disabled={
											!!getDocumentByStep(type)?.sent_at || isSendingDocument
										}
										incompleted={getIsSendComplaintDisabled()}
										decoration="primary"
										size="medium"
										buttonLabel="Confirm"
										onClickConfirm={() => handleSendDocument(type)}
										onClickButton={() => {
											if (getIsSendComplaintDisabled()) {
												setIsErrorMessage(true);
												if (isApproved) {
													handleSetErrorMessage();
												}
											}
										}}
										label={
											getDocumentByStep(type)?.sent_at ? type : `Send ${type}`
										}
										confirmMessage={`Confirm to send ${type}.`}
										buttonIcon={Icons.checkWhite}
										buttonIconRight={!!getDocumentByStep(type)?.sent_at}
									/>
								</React.Fragment>
							)
						);
					})}

					{!getIsSendOptHidden() && (
						<ButtonDropdownConfirm
							label={optByStep?.sent_at ? 'Opt Out' : 'Send Opt Out'}
							confirmMessage="Confirm to send Opt Out Request."
							buttonLabel="Confirm"
							onClickConfirm={handleSendOptRequest}
							onClickButton={() => {
								(!caseDocumentByStep?.sent_at || !isApproved) &&
									setIsErrorMessage(true);
							}}
							decoration="primary"
							disabled={!!optByStep?.sent_at}
							incompleted={!caseDocumentByStep?.sent_at || !isApproved}
							classNameDropdownMenuContent="w-[205px]"
							buttonIcon={Icons.checkWhite}
							buttonIconRight={!!optByStep?.sent_at}
						/>
					)}
					<ButtonDropdownConfirm
						incompleted={getIsCaseUnsuccessfulDisabled()}
						disabled={caseDetails?.progress_name === 'Case Unsuccessful'}
						label="Case Unsuccessful"
						confirmMessage="Confirm Case Unsuccessful"
						buttonLabel="Confirm"
						onClickConfirm={handleUnsuccessful}
						onClickButton={() => {
							getIsCaseUnsuccessfulDisabled() && setIsErrorMessage(true);
						}}
						decoration="primary"
						classNameDropdownMenuContent="w-[205px]"
						buttonIcon={Icons.checkWhite}
						buttonIconRight={caseDetails?.progress_name === 'Case Unsuccessful'}
					/>
				</>
			)}

			<Button
				disabled={getIsPhaseMoved()}
				className={clsx({
					hidden: !isMovedToNextPhase,
					'!bg-[#B18EE0] !border-[#B18EE0] hover:!bg-[#B18EE0] focus:!bg-[#B18EE0] cursor-default':
						getIsMoveNextPhaseDisabled(),
				})}
				decoration="primary"
				size="medium"
				classNameButton="!size-4"
				onClick={() => {
					getIsMoveNextPhaseDisabled()
						? setIsErrorMessage(true)
						: handleMoveToTheNextPhase();
				}}
				label="Continue to Next Phase"
				icon={Icons.checkWhite}
				iconRight={getIsPhaseMoved()}
			/>
			{title === 'Settlement Phase' && (
				<>
					<ButtonDropdownConfirm
						incompleted={getIsSentSettlementAgreementDisabled()}
						disabled={!!caseDetails?.date_settlement_sent}
						label="Send Settlement Agreement"
						confirmMessage="Confirm to send Settlement Agreement"
						buttonLabel="Confirm"
						onClickConfirm={handleSendSettlementAgreement}
						onClickButton={() => {
							// getIsSentSettlementAgreementDisabled() && setIsErrorMessage(true);
							if (getIsSentSettlementAgreementDisabled()) {
								setIsErrorMessage(true);
								if (isApproved) {
									handleSetErrorMessage();
								}
							}
						}}
						decoration="primary"
						classNameDropdownMenuContent="w-[251px] !px-3"
						buttonIcon={Icons.checkWhite}
						buttonIconRight={!!caseDetails?.date_settlement_sent}
					/>
					<div className="flex items-center gap-1">
						<ButtonDropdownConfirm
							incompleted={getIsPaySettlementDisabled()}
							disabled={!!caseDetails?.date_payment_sent}
							onClickButton={() => {
								getIsPaySettlementDisabled() && setIsErrorMessage(true);
							}}
							label="Pay Settlement"
							confirmMessage="Confirm to Pay Settlement"
							buttonLabel="Confirm"
							onClickConfirm={handlePaySettlement}
							decoration="primary"
							classNameDropdownMenuContent="w-[251px] !px-3"
							buttonIcon={Icons.checkWhite}
							buttonIconRight={!!caseDetails?.date_payment_sent}
							className={clsx({
								hidden:
									settlementDetail?.payment_method ===
									PaymentMethodEnum.EXTERNAL,
							})}
						/>
						{hasPaymentError && <ErrorIcon tooltipText="" />}
					</div>
					<Button
						disabled={!!caseDetails?.date_case_resolved}
						label="Case Resolved"
						onClick={() => {
							getIsCaseResolvedDisabled()
								? setIsErrorMessage(true)
								: handleCaseResolved();
						}}
						decoration="primary"
						icon={Icons.checkWhite}
						iconRight={!!caseDetails?.date_case_resolved}
						size="fit"
						className={clsx({
							'!bg-[#B18EE0] !border-[#B18EE0] hover:!bg-[#B18EE0] focus:!bg-[#B18EE0] cursor-default':
								getIsCaseResolvedDisabled(),
						})}
					/>
				</>
			)}
		</div>
	);
};
